import { msg } from '@omegagrid/localize';
import rp, {
	Service, SqlEditorAdapter, SqlEditorOptions, SqlExecOptions, helpers,
	SqlCompileResult, SqlExecCallbackArgs, SqlExportFormat, SqlParseResult,
	CodeLinkProvider, createSqlIncludeLinkProvider, SqlIncludeMacro } from "@reporting/rp-app-host";
import { HistoryItem } from '@reporting/rp-types/core';
import { Thread } from "@reporting/rp-types/etl";
import { CsvDescription } from '@reporting/rp-utils-csv';

export class ThreadSqlEditorAdapter extends SqlEditorAdapter {

	private editable: boolean;
	private scope: number = 0;

	constructor(
		protected etlClient: Service,
		protected thread: Thread,
		protected getParameterValues: () => Record<string, unknown> = null
	) { 
		super();
		this.scope = this.thread.accounting === rp.accountingProvider.a ? 0 : 1;
		this.editable = this.scope === 0;
	}

	async getContent() : Promise<string> {
		if (this.thread.id_thread == 0) return null;
		return this.etlClient.get<string>(`threads/${this.thread.id_thread}/content`, { scope: this.scope });
	}

	async getHistoryContent(idz: number): Promise<string> {
		return this.etlClient.get<string>(`threads/${this.thread.id_thread}/history/${idz}`, { scope: this.scope });
	}

	/** Ctrl+click on the thread name of a `{{include}}` / `{{include_once}}` macro opens that thread. */
	getLinkProvider(): CodeLinkProvider {
		return createSqlIncludeLinkProvider({
			tooltip: msg('Open'),
			open: macro => this.openIncludedThread(macro),
		});
	}

	/**
	 * Looks the included thread up the way p_ETL_EXEC_THREAD does (DESCRIPTION within the accounting the macro
	 * names, otherwise the one the thread runs under) and opens its editor tab.
	 */
	private async openIncludedThread(macro: SqlIncludeMacro) {
		const accounting = macro.accounting ?? rp.accountingProvider.a;
		const threads = await this.etlClient.get<Thread[]>('threads', {
			fields: ['id_thread', 'description', 'accounting'],
			where: { DESCRIPTION: macro.name, ACCOUNTING: accounting },
			// shared (-1) threads lie outside the accounting vector the list is restricted to
			includeShared: true,
			count: 1,
		});
		const thread = threads?.[0];
		if (!thread) {
			rp.host.showAlert({
				type: 'warning',
				title: 'SQL Editor',
				content: msg("Source '{name}' was not found.", { data: { name: macro.name } }),
			});
			return;
		}
		await rp.host.open(`etl-thread-${thread.id_thread}`, { title: thread.description });
	}

	async parse(sql: string) : Promise<SqlParseResult> {
		return this.etlClient.post<SqlParseResult>(`parse/${this.thread.id_thread}`, sql, { scope: this.scope });
	}

	async compile(sql: string) : Promise<SqlCompileResult> {
		const response = await this.etlClient.postRaw(`compile/${this.thread.id_thread}`, sql, { scope: this.scope });
		return {
			sql: await this.etlClient.handleResponse<string>(response, false),
			offset: parseInt(response.headers.get('X-Error-Offset')) || 0
		};
	}

	async exec(sql: string, options?: SqlExecOptions, cb?: (args: SqlExecCallbackArgs) => void) : Promise<void> {
		const params = this.getParameterValues ? this.getParameterValues() : {};
		this.lastExec = { sql, options, params };
		await this.execStream(this.etlClient, `exec/${this.thread.id_thread}`, sql, options, cb, {
			scope: this.scope,
			customParams: params
		});
	}

	async export(format: SqlExportFormat, datasetCount: number) : Promise<void> {
		if (!this.lastExec) return;
		const { sql, params } = this.lastExec;
		// CSV is single-table: more than one dataset comes back as a .zip with one .csv per dataset.
		const ext = format === 'xlsx' ? 'xlsx' : (datasetCount > 1 ? 'zip' : 'csv');
		const csvParams: CsvDescription | undefined = format === 'csv'
			? { delimiter: ',', quote: '"', header: true, headerTypes: false, headerJson: false, encoding: 'UTF-8', nullValue: '' }
			: undefined;
		rp.host.showAlert({title: msg('Export'), content: msg('Export started.'), type: 'info'});
		await this.etlClient.downloadPost(`exec/${this.thread.id_thread}/export`, sql, {
			scope: this.scope,
			format,
			top: 0,
			...(csvParams ? { csvParams } : {}),
			customParams: params ?? {},
			fileName: helpers.createExportFilename(ext),
		});
	}

	async save(sql: string) {
		await this.etlClient.patch(`threads/${this.thread.id_thread}/content`, sql);
	}

	async abort() {
		await this.abortSpid(this.etlClient);
	}

	getHistoryItems(): Promise<HistoryItem[]> {
		return this.etlClient.get<HistoryItem[]>(`threads/${this.thread.id_thread}/history`);
	}

	getCurrentItem(): HistoryItem {
		return this.thread;
	}

	getOptions(): SqlEditorOptions {
		return this.createOptions({
			history: this.editable,
			toolbar: {
				save: { enabled: this.editable },
				discard: { enabled: this.editable },
			}
		});
	}

}
